// OUs.tsx
import { useState, useEffect, useCallback } from "react";
import {
    RefreshCw, FolderTree, Globe, User,
    Monitor, Users, Link, Shield, Calendar,
    Loader2, Search, ChevronLeft, ChevronRight,
    ChevronsLeft, ChevronsRight, CheckCircle2, XCircle, Fingerprint
} from "lucide-react";
import baseapi from "../../services/baseapi";
import { showError } from "../../utils/toast";
import { useClientLevel } from "../../hooks/useClientLevel";
import { ItemsPerPageDropdown } from "../../components/ItemsPerPageDropdown ";

interface ADOU {
    id: number;
    created: string;
    updated: string;
    agent_id: number;
    client_id: number;
    asset_id: number;
    ad_domain_guid: string;
    domain: string;
    ou: string;
    name: string;
    managed_by: string;
    modified_when: string;
    created_when: string;
    distinguished_name: string;
    object_guid: string;
    empty: boolean;
    gp_link: string;
    is_critical_system_object: boolean;
    linked_computers: string;
    linked_groups: string;
    linked_users: string;
}

// Safely parse a JSON string array into a list
const parseJsonArray = (raw: unknown): string[] => {
    if (typeof raw === "string") {
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }

    return Array.isArray(raw) ? raw : [];
};

// Safely parse GP links
interface GPLink { name: string; enabled: boolean; enforced: boolean; }
const parseGPLinks = (raw: unknown): GPLink[] => {
    if (!raw) return [];

    if (typeof raw === "string") {
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }

    return Array.isArray(raw) ? raw : [];
};

export default function OUs({ asset_id }: { asset_id: any }) {
    const [ous, setOUs] = useState<ADOU[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [expandedRow, setExpandedRow] = useState<number | null>(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);

    const level = useClientLevel();

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchOUs = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (asset_id) queryObj["asset_id"] = asset_id;

            if (debouncedSearch) {
                queryObj.$or = [
                    { name: { $ilike: `%${debouncedSearch}%` } },
                    { ou: { $ilike: `%${debouncedSearch}%` } },
                    { domain: { $ilike: `%${debouncedSearch}%` } },
                    { distinguished_name: { $ilike: `%${debouncedSearch}%` } },
                    { managed_by: { $ilike: `%${debouncedSearch}%` } },
                ];
            }

            const response = await baseapi.get("/ad_ous", {
                params: {
                    query: JSON.stringify(queryObj),
                    skip: (currentPage - 1) * limit,
                    limit
                }
            });

            if (response.data.status) {
                setOUs(response.data.data);
                setTotalRecords(response.data.total ?? response.data.data.length);
            } else {
                showError("Failed to load OUs");
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    }, [level, debouncedSearch, currentPage, limit]);

    useEffect(() => {
        fetchOUs();
    }, [fetchOUs]);

    const Cell = ({ value }: { value: string }) => (
        <span className={!value || value === "-" ? " text-gray-200" : "text-gray-100"}>
            {value || "-"}
        </span>
    );

    const LinkedBadges = ({ raw, color }: { raw: string; color: string }) => {
        const items = parseJsonArray(raw);
        if (items.length === 0) return <span className=" text-gray-200 text-[10px]">—</span>;
        return (
            <div className="flex flex-wrap gap-1">
                {items.map((item, i) => (
                    <span key={i} className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${color}`}>
                        {item}
                    </span>
                ))}
            </div>
        );
    };

    const totalPages = Math.ceil(totalRecords / limit);
    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endRecord = Math.min(currentPage * limit, totalRecords);

    return (
        <div className="flex flex-col bg-transparent text-gray-200">
            {/* Action Bar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-2">
                <div className="flex items-center gap-2">
                    <div className="bg-blue-600/20 p-1.5 rounded-lg border border-blue-500/30">
                        <FolderTree size={16} className="text-blue-400" />
                    </div>
                    <h1 className="text-lg font-bold text-white tracking-wide">Organizational Units</h1>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative w-56 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-200 group-focus-within:text-blue-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search OUs..."
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 transition-all"
                        />
                    </div>
                    <button
                        onClick={fetchOUs}
                        disabled={loading}
                        className="p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group shrink-0"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-blue-400"} />
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl overflow-hidden shadow-2xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-gray-700/50">
                                <th className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest">
                                    <div className="flex items-center gap-2"><FolderTree size={13} className="text-blue-400" /> Name</div>
                                </th>
                                <th className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest">
                                    <div className="flex items-center gap-2"><Fingerprint size={13} className="text-teal-400" /> Distinguished Name</div>
                                </th>
                                <th className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest">
                                    <div className="flex items-center gap-2"><Globe size={13} className="text-indigo-400" /> Domain</div>
                                </th>
                                <th className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest">
                                    <div className="flex items-center gap-2"><User size={13} className="text-cyan-400" /> Managed By</div>
                                </th>
                                <th className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest">
                                    <div className="flex items-center gap-2"><Monitor size={13} className="text-emerald-400" /> Computers</div>
                                </th>
                                <th className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest">
                                    <div className="flex items-center gap-2"><Users size={13} className="text-yellow-400" /> Groups</div>
                                </th>
                                <th className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest">
                                    <div className="flex items-center gap-2"><User size={13} className="text-pink-400" /> Users</div>
                                </th>
                                <th className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest">
                                    <div className="flex items-center gap-2"><Link size={13} className="text-orange-400" /> GP Links</div>
                                </th>
                                <th className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest">
                                    <div className="flex items-center gap-2"><Shield size={13} className="text-red-400" /> Critical</div>
                                </th>
                                <th className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest">
                                    <div className="flex items-center gap-2"><Calendar size={13} className="text-orange-400" /> Created</div>
                                </th>
                                <th className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest">
                                    <div className="flex items-center gap-2"><Calendar size={13} className="text-purple-400" /> Modified</div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={11} className="py-16 text-center">
                                        <div className="flex items-center justify-center gap-2 text-gray-500">
                                            <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">Loading OUs...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : ous.length === 0 ? (
                                <tr>
                                    <td colSpan={11} className="py-16 text-center">
                                        <div className="flex flex-col items-center gap-2 opacity-40">
                                            <FolderTree size={24} className="text-gray-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">
                                                {debouncedSearch ? `No results for "${debouncedSearch}"` : "No OUs found"}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                ous.map((ou) => {
                                    const gpLinks = parseGPLinks(ou.gp_link);
                                    const isExpanded = expandedRow === ou.id;

                                    return (
                                        <>
                                            <tr
                                                key={ou.id}
                                                onClick={() => setExpandedRow(isExpanded ? null : ou.id)}
                                                className="hover:bg-gray-800/60 transition-colors cursor-pointer group"
                                            >
                                                {/* Name */}
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <FolderTree size={13} className="text-blue-400 shrink-0" />
                                                        <span className="text-[11px] font-bold text-gray-100">{ou.name || "-"}</span>
                                                        {ou.empty && (
                                                            <span className="text-[9px] text-gray-500 font-bold uppercase bg-gray-800 px-1 rounded border border-gray-700">Empty</span>
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Distinguished Name */}
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className="text-[10px] font-mono text-gray-300 block" title={ou.distinguished_name}>
                                                        <Cell value={ou.distinguished_name} />
                                                    </span>
                                                </td>

                                                {/* Domain */}
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className="text-[11px] text-gray-300">{ou.domain || "-"}</span>
                                                </td>

                                                {/* Managed By */}
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <Cell value={ou.managed_by} />
                                                </td>

                                                {/* Computers */}
                                                <td className="px-4 py-3">
                                                    <LinkedBadges
                                                        raw={ou.linked_computers}
                                                        color="bg-emerald-500/10 text-emerald-300 border-emerald-500/20"
                                                    />
                                                </td>

                                                {/* Groups */}
                                                <td className="px-4 py-3">
                                                    <LinkedBadges
                                                        raw={ou.linked_groups}
                                                        color="bg-yellow-500/10 text-yellow-300 border-yellow-500/20"
                                                    />
                                                </td>

                                                {/* Users */}
                                                <td className="px-4 py-3">
                                                    <LinkedBadges
                                                        raw={ou.linked_users}
                                                        color="bg-pink-500/10 text-pink-300 border-pink-500/20"
                                                    />
                                                </td>

                                                {/* GP Links count */}
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    {gpLinks.length > 0 ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-500/10 border border-orange-500/20 rounded text-[10px] font-bold text-orange-300">
                                                            <Link size={10} /> {gpLinks.length} linked
                                                        </span>
                                                    ) : (
                                                        <span className=" text-gray-200 text-[10px]">—</span>
                                                    )}
                                                </td>

                                                {/* Critical */}
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    {ou.is_critical_system_object ? (
                                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-400">
                                                            <CheckCircle2 size={12} /> Yes
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold  text-gray-200">
                                                            <XCircle size={12} /> No
                                                        </span>
                                                    )}
                                                </td>

                                                {/* Created */}
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className="text-[10px] font-mono text-gray-200"><Cell value={ou.created_when} /></span>
                                                </td>

                                                {/* Modified */}
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className="text-[10px] font-mono text-gray-200"><Cell value={ou.modified_when} /></span>
                                                </td>
                                            </tr>

                                            {/* Expanded GP Links detail row */}
                                            {isExpanded && gpLinks.length > 0 && (
                                                <tr key={`${ou.id}-expanded`} className="bg-gray-900/60">
                                                    <td colSpan={11} className="px-6 py-3">
                                                        <p className="text-[9px] font-bold text-orange-400 uppercase tracking-widest mb-2 flex items-center gap-1">
                                                            <Link size={10} /> GP Links
                                                        </p>
                                                        <div className="flex flex-wrap gap-2">
                                                            {gpLinks.map((gp, i) => (
                                                                <div key={i} className="flex items-center gap-2 px-2 py-1 bg-gray-800 border border-gray-700 rounded-lg">
                                                                    <span className="text-[10px] font-bold text-gray-200">{gp.name}</span>
                                                                    <span className={`text-[9px] font-bold px-1 rounded ${gp.enabled ? "text-green-400 bg-green-500/10" : "text-red-400 bg-red-500/10"}`}>
                                                                        {gp.enabled ? "Enabled" : "Disabled"}
                                                                    </span>
                                                                    {gp.enforced && (
                                                                        <span className="text-[9px] font-bold px-1 rounded text-yellow-400 bg-yellow-500/10">Enforced</span>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/40 border-t border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => { setLimit(v); setCurrentPage(1); }}
                        />
                        <span className="text-[9px] font-medium text-gray-500 tracking-widest">
                            <span className="text-gray-300">{startRecord}</span>
                            {" - "}
                            <span className="text-gray-300">{endRecord}</span>
                            <span className="mx-1  text-gray-200">/</span>
                            <span className="text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                        {debouncedSearch && (
                            <button
                                onClick={() => setSearchTerm("")}
                                className="text-[9px] text-blue-400 hover:text-blue-300 font-bold uppercase tracking-widest transition-colors"
                            >
                                Clear search
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsLeft size={14} />
                        </button>
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded text-[10px] font-bold mx-1">
                            {currentPage}
                        </div>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronRight size={14} />
                        </button>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
